import { FormControl, FormControlProps, Stack } from "@/storybook-components";
import { QuestionFooter } from "./question-footer";
import { QuestionTitle } from "./question-title";
import { BaseQuestionProps } from "./types";

interface QuestionLayoutProps extends FormControlProps {
  spacing: number;
  direction: "row" | "column";
}
const Question = (props: QuestionLayoutProps) => {
  const { spacing, direction, ...rest } = props;
  return (
    <FormControl {...rest}>
      <Stack spacing={spacing || 0} direction={direction}>
        {props.children}
      </Stack>
    </FormControl>
  );
};

const QuestionWithHeaderAndFooter = (props: BaseQuestionProps) => {
  const {
    children,
    spacing,
    direction = "column",
    isRequired,
    isInvalid,
    labelClassName,
    layoutProps,
    className,
    questionId,
    questionLabel,
    accessibilityTextOfRequired,
    questionLabelType = "label",
    questionLabelProps,
    footerStackSpacing,
    answerLength,
    note,
    maxLength,
    maxNote,
    errorMessage,
    errorMessageProps,
    withErrorIcon,
    noteProps
  } = props;

  return (
    <Question
      spacing={spacing}
      direction={direction}
      isRequired={isRequired}
      isInvalid={isInvalid}
      className={className}
      {...layoutProps}
    >
      {typeof questionLabel === "string" ? (
        <QuestionTitle
          isRequired={isRequired}
          accessibilityTextOfRequired={accessibilityTextOfRequired}
          labelType={questionLabelType}
          id={`${questionLabelType}-${questionId}`}
          htmlFor={questionId}
          labelClassName={labelClassName}
          {...questionLabelProps}
        >
          {questionLabel}
        </QuestionTitle>
      ) : (
        questionLabel
      )}
      {children}
      <QuestionFooter
        spacing={footerStackSpacing}
        answerLength={answerLength}
        notes={note}
        maxLength={maxLength}
        maxLengthNote={maxNote}
        error={errorMessage}
        errorMsgStyles={errorMessageProps}
        helperTextStyles={noteProps}
        withErrorIcon={withErrorIcon}
      />
    </Question>
  );
};

const questionCommonStorybookArgs = {
  direction: {
    defaultValue: "column",
    control: {
      type: "hidden"
    },
    table: {
      type: { summary: "text" }
    }
  },
  labelType: {
    defaultValue: "label",
    control: {
      type: "hidden"
    },
    table: {
      type: { summary: "label | legend" }
    }
  },
  spacing: {
    defaultValue: 0,
    control: {
      type: "number",
      min: 0
    },
    table: {
      type: { summary: "number" }
    }
  },
  isRequired: {
    defaultValue: false,
    control: {
      type: "boolean"
    },
    table: {
      type: { summary: "boolean" }
    }
  },
  isInvalid: {
    defaultValue: false,
    control: {
      type: "boolean"
    },
    table: {
      type: { summary: "boolean" }
    }
  },
  questionId: {
    defaultValue: "abc123",
    control: {
      type: "hidden"
    },
    table: {
      type: { summary: "string" }
    }
  },

  accessibilityTextOfRequired: {
    defaultValue: "* required",
    control: {
      type: "text"
    },
    table: {
      type: { summary: "string" }
    }
  },
  questionLabelProps: {
    defaultValue: {
      fontSize: "1.125rem",
      lineHeight: "123.5%",
      fontWeight: "medium",
      color: "gray.800"
    },
    control: {
      type: "object"
    },
    table: {
      type: { summary: "object" }
    }
  },
  placeholder: {
    defaultValue: "",
    control: {
      type: "text"
    },
    table: {
      type: { summary: "any" }
    }
  },
  isDisabled: {
    defaultValue: false,
    control: {
      type: "boolean"
    },
    table: {
      type: { summary: "boolean" }
    }
  },
  footerStackSpacing: {
    defaultValue: 2,
    control: {
      type: "number",
      min: 0
    },
    table: {
      type: { summary: "number" }
    }
  },
  note: {
    defaultValue: "Question Notes",
    control: {
      type: "text"
    },
    table: {
      type: { summary: "string" }
    }
  },
  maxNote: {
    defaultValue: "maximum 255 characters",
    control: {
      type: "text"
    },
    table: {
      type: { summary: "string" }
    }
  },
  maxLength: {
    control: {
      type: "number",
      min: 1
    },
    table: {
      type: { summary: "number" }
    }
  },
  errorMessage: {
    defaultValue: "Invalid input",
    control: {
      type: "text"
    },
    table: {
      type: { summary: "string" }
    }
  },
  errorMessageProps: {
    defaultValue: {
      fontSize: "0.75rem",
      lineHeight: "133%",
      fontWeight: "normal",
      letterSpacing: "0.2px",
      color: "red.600"
    },
    control: {
      type: "object"
    },
    table: {
      type: { summary: "object" }
    }
  },
  noteProps: {
    defaultValue: {
      fontSize: "0.75rem",
      lineHeight: "133%",
      fontWeight: "normal",
      letterSpacing: "0.2px",
      color: "gray.600"
    },
    control: {
      type: "object"
    },
    table: {
      type: { summary: "object" }
    }
  }
};

export { Question, QuestionWithHeaderAndFooter, questionCommonStorybookArgs };
